import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";

/**
 * Very small, dependency-free markdown renderer for the policy pages.
 * Handles: # / ## / ###, blank-line paragraphs, - list bullets, *italic*,
 * **bold**, and `code`. It is intentionally minimal — we control the policy
 * source and do not accept user-provided markdown here.
 */
function renderMd(md) {
  const lines = md.split(/\r?\n/);
  const out = [];
  let buffer = [];
  let listBuffer = [];

  const flushParagraph = () => {
    if (buffer.length) {
      const text = buffer.join(" ");
      out.push(<p key={out.length} className="text-sm sm:text-base leading-relaxed text-fg-secondary mt-4 break-words" dangerouslySetInnerHTML={{ __html: inline(text) }} />);
      buffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer.length) {
      out.push(
        <ul key={out.length} className="mt-4 list-disc pl-6 space-y-2">
          {listBuffer.map((item, i) => (
            <li key={i} className="text-sm sm:text-base leading-relaxed text-fg-secondary break-words" dangerouslySetInnerHTML={{ __html: inline(item) }} />
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };

  const inline = (t) =>
    t
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/`([^`]+)`/g, '<code class="px-1 rounded bg-bg-surface-hover text-fg-primary text-xs break-all">$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-fg-primary font-medium">$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (line.startsWith("### ")) {
      flushParagraph(); flushList();
      out.push(<h3 key={out.length} className="mt-8 text-lg font-heading font-medium text-fg-primary">{line.slice(4)}</h3>);
    } else if (line.startsWith("## ")) {
      flushParagraph(); flushList();
      out.push(<h2 key={out.length} className="mt-10 text-xl sm:text-2xl font-heading font-medium text-fg-primary">{line.slice(3)}</h2>);
    } else if (line.startsWith("# ")) {
      flushParagraph(); flushList();
      out.push(<h1 key={out.length} className="text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary">{line.slice(2)}</h1>);
    } else if (line.startsWith("- ")) {
      flushParagraph();
      listBuffer.push(line.slice(2));
    } else if (line.trim() === "") {
      flushParagraph(); flushList();
    } else {
      flushList();
      buffer.push(line);
    }
  }
  flushParagraph(); flushList();
  return out;
}

export function PolicyPage({ policyType, title, testid }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/policies/${policyType}`);
        setDoc(data);
      } catch (err) {
        setError("Policy not available.");
      }
    })();
  }, [policyType]);

  return (
    <div data-testid={testid} className="min-h-screen bg-bg-base text-fg-primary flex flex-col">
      <header className="border-b border-border-subtle bg-bg-surface">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 min-h-16 py-2 flex flex-wrap items-center justify-between gap-y-2 gap-x-2">
          <Link to="/" className="min-w-0"><Wordmark size="sm" testid={`${testid}-wordmark`} /></Link>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm text-fg-secondary">
            <Link to="/plans" data-testid={`${testid}-nav-pricing`} className="hover:text-fg-primary transition-colors duration-200">Pricing</Link>
            <Link to="/privacy" data-testid={`${testid}-nav-privacy`} className="hover:text-fg-primary transition-colors duration-200">Privacy</Link>
            <Link to="/terms" data-testid={`${testid}-nav-terms`} className="hover:text-fg-primary transition-colors duration-200">Terms</Link>
            <Link to="/refund-policy" data-testid={`${testid}-nav-refund`} className="hover:text-fg-primary transition-colors duration-200">Refund</Link>
            <Link to="/login" data-testid={`${testid}-nav-login`} className="hover:text-fg-primary transition-colors duration-200">Log in</Link>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          {doc && (
            <>
              <p className="text-xs text-fg-secondary break-words" data-testid={`${testid}-version`}>
                {title} · Version {doc.version} · Effective {new Date(doc.effective_at).toLocaleDateString()}
              </p>
              <article data-testid={`${testid}-content`} className="mt-4 break-words">
                {renderMd(doc.content)}
              </article>
            </>
          )}
          {error && (
            <p className="text-sm text-fg-secondary" data-testid={`${testid}-error`}>{error}</p>
          )}
        </div>
      </main>
    </div>
  );
}

export default PolicyPage;
